import { nextTick } from 'vue';
import {
  Composer,
  createI18n,
  I18nOptions,
} from 'vue-i18n';

import enUS from './en-US.json';

import { container } from '@/container';
import { Logger } from '@/services/Logger';

const logger = container.get(Logger);

type MessageSchema = typeof enUS;

export const DEFAULT_LOCALE = 'en-US';

export const SUPPORTED_LOCALES_MAP: Record<string, string> = {
  [DEFAULT_LOCALE]: 'English',
  fr: 'Français',
};

export type SUPPORTED_LOCALES = keyof typeof SUPPORTED_LOCALES_MAP;

const options: I18nOptions = {
  datetimeFormats: {
    [DEFAULT_LOCALE]: {
      short: {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
      },
      long: {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        weekday: 'short',
        hour: 'numeric',
        minute: 'numeric',
      },
    },
    fr: {
      short: {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
      },
      long: {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        weekday: 'short',
        hour: 'numeric',
        minute: 'numeric',
      },
    },
  },
  legacy: false,
  fallbackLocale: DEFAULT_LOCALE,
  fallbackWarn: false,
  messages: {
    [DEFAULT_LOCALE]: enUS,
  },
  missingWarn: false,
  warnHtmlMessage: false,
};

export const i18n = createI18n(options);

// Export common methods.
// eslint-disable-next-line @typescript-eslint/unbound-method
export const { t, te, d } = i18n.global;

export function getActiveLocale(): string {
  return (i18n.global as unknown as Composer).locale.value;
}

export function hasLocale(locale: string): boolean {
  const messages = i18n.global.getLocaleMessage(locale);
  return !!(messages && Object.keys(messages).length);
}

/**
 * Lazy loaders for each supported locale.
 *
 * These must be static string literals so the bundler can code-split them into
 * their own chunks. A variable path (`./${locale}.json`) cannot be analyzed
 * because the files sit in the same directory as this module.
 */
const localeLoaders: Record<string, () => Promise<unknown>> = {
  // The default locale is bundled with the entry point, so there is nothing to fetch.
  [DEFAULT_LOCALE]: () => Promise.resolve(enUS),
  fr: async () => (await import('./fr.json')).default,
};

async function loadLocaleMessages(locale: string) {
  try {
    const loader = localeLoaders[locale];
    if (!loader) {
      throw new Error(`Unsupported locale: ${locale}`);
    }

    const messages = <MessageSchema> await loader();
    i18n.global.setLocaleMessage(locale, messages);
  }
  catch (error) {
    logger.captureException(<Error>error, { tags: { locale } });
    throw new Error(`Unable to load locale: ${locale}`);
  }

  return nextTick();
}

export type ILocaleChangeListener = (newLocale: string, oldLocale: string) => void;
const localChangeListeners: ILocaleChangeListener[] = [];
export function onLocaleChange(listener: ILocaleChangeListener) {
  localChangeListeners.push(listener);
}

export async function setLocale(locale: string) {
  const previousLocale = getActiveLocale();
  await loadLocaleMessages(locale);
  (i18n.global as unknown as Composer).locale.value = locale;
  if (typeof document !== 'undefined') {
    document.documentElement?.setAttribute('locale', locale);
  }
  localChangeListeners.forEach((listener) => listener(locale, previousLocale));
}
